import { useMemo, useState } from 'react';
import { Card } from '@/components/ui/card';
import Kpi from '@/components/ui/Kpi';
import DateRangePicker, { createPresetRange, type DateRange } from '@/components/filters/DateRangePicker';
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
} from 'recharts';
import { useSalesData } from '@/hooks/useSalesData';
import { supabase } from '@/lib/supabase';

const currencyFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 0,
});

const numberFormatter = new Intl.NumberFormat('en-US');

const pieColors = ['#38bdf8', '#22c55e', '#f97316', '#a855f7', '#facc15', '#ef4444'];

const SalesPage = () => {
  const [range, setRange] = useState<DateRange>(() => createPresetRange(30));
  const { data, isLoading, error } = useSalesData(range);

  const summary = useMemo(() => {
    if (!data) {
      return {
        revenue: '$0',
        orders: '0',
        aov: '$0',
      };
    }

    return {
      revenue: currencyFormatter.format(data.summary.revenue),
      orders: numberFormatter.format(data.summary.orders),
      aov: currencyFormatter.format(data.summary.averageOrderValue),
    };
  }, [data]);

  const hasSupabase = Boolean(supabase);
  const revenueSeries = data?.revenueSeries ?? [];
  const orderSeries = data?.orderSeries ?? [];
  const paymentMix = data?.paymentMethodMix ?? [];

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-semibold text-white">Sales & Revenue</h2>
          <p className="text-sm text-slate-400">Track revenue cadence, order volume, and tender mix.</p>
        </div>
        <DateRangePicker value={range} onChange={setRange} />
      </div>

      {!hasSupabase && (
        <Card title="Configuration Required" className="border-yellow-600/60 bg-yellow-500/10 text-yellow-100">
          <p className="text-sm">Provide Supabase environment variables to view live sales data.</p>
        </Card>
      )}

      {error && (
        <Card title="Error" className="border-red-500/60 bg-red-500/10 text-red-100">
          <p className="text-sm">{error.message}</p>
        </Card>
      )}

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        <Kpi label="Revenue" value={summary.revenue} isLoading={isLoading} />
        <Kpi label="Orders" value={summary.orders} isLoading={isLoading} />
        <Kpi label="Average Order Value" value={summary.aov} isLoading={isLoading} />
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <Card title="Revenue by Day" className="h-[320px]">
          {isLoading ? (
            <div className="h-full animate-pulse rounded-lg bg-slate-800/50" />
          ) : revenueSeries.length ? (
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={revenueSeries}>
                <defs>
                  <linearGradient id="sales-revenue" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#38bdf8" stopOpacity={0.8} />
                    <stop offset="95%" stopColor="#38bdf8" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#1f2937" />
                <XAxis dataKey="label" stroke="#94a3b8" />
                <YAxis stroke="#94a3b8" tickFormatter={(value) => currencyFormatter.format(value)} />
                <Tooltip formatter={(value: number) => currencyFormatter.format(value)} />
                <Area type="monotone" dataKey="value" stroke="#38bdf8" fillOpacity={1} fill="url(#sales-revenue)" />
              </AreaChart>
            </ResponsiveContainer>
          ) : (
            <p className="text-sm text-slate-400">No revenue data for this range.</p>
          )}
        </Card>
        <Card title="Orders by Day" className="h-[320px]">
          {isLoading ? (
            <div className="h-full animate-pulse rounded-lg bg-slate-800/50" />
          ) : orderSeries.length ? (
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={orderSeries}>
                <CartesianGrid strokeDasharray="3 3" stroke="#1f2937" />
                <XAxis dataKey="label" stroke="#94a3b8" />
                <YAxis stroke="#94a3b8" />
                <Tooltip />
                <Bar dataKey="value" fill="#a855f7" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <p className="text-sm text-slate-400">No order data for this range.</p>
          )}
        </Card>
      </div>

      <Card title="Payment Method Mix">
        {isLoading ? (
          <div className="h-48 animate-pulse rounded-lg bg-slate-800/50" />
        ) : paymentMix.length ? (
          <div className="grid gap-6 md:grid-cols-2">
            <ResponsiveContainer width="100%" height={240}>
              <PieChart>
                <Pie data={paymentMix} dataKey="total" nameKey="method" innerRadius={50} outerRadius={90} paddingAngle={2}>
                  {paymentMix.map((entry, index) => (
                    <Cell key={entry.method} fill={pieColors[index % pieColors.length]} />
                  ))}
                </Pie>
                <Tooltip formatter={(value: number) => currencyFormatter.format(value)} />
              </PieChart>
            </ResponsiveContainer>
            <ul className="space-y-2 text-sm text-slate-300">
              {paymentMix.map((item, index) => (
                <li key={item.method} className="flex items-center justify-between gap-3">
                  <span className="flex items-center gap-2">
                    <span
                      className="inline-block h-2.5 w-2.5 rounded-full"
                      style={{ backgroundColor: pieColors[index % pieColors.length] }}
                    />
                    {item.method}
                  </span>
                  <span className="font-mono text-slate-400">{currencyFormatter.format(item.total)}</span>
                </li>
              ))}
            </ul>
          </div>
        ) : (
          <p className="text-sm text-slate-400">No settled transactions for this range.</p>
        )}
      </Card>
    </div>
  );
};

export default SalesPage;
